import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Stack } from '@startuptools/ui/Stack';
import { LabeledText } from '@startuptools/ui/LabeledText';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Card } from '@startuptools/ui/Card';
import { IconButton } from '@startuptools/ui/IconButton';
import { ToggleButtonGroup } from '@startuptools/ui/ToggleButtonGroup';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  type useCreateOptionsSharesSubscriberRepresentativeMutation,
  type useDeleteOptionsSharesSubscriberRepresentativeMutation,
  type useOssDeleteMutation,
  type useOssSetPaymentStatusMutation,
} from '../../../../../../graphql/react-operations';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { PaymentStatus } from '@startuptools/common/participants';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import * as React from 'react';
import { IDKind, JuridicalKind } from '../../../../../../models/identity.model';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { type GqlStepOsrSubscribersQuery } from './options-subscribers.graphql';
import { OptionsSharesSubscriptionNote } from '../../../../../common/components/documents/OptionsSharesSubscriptionNote';
import { type GqlMeetingConcern, type GqlWarrantProgram } from '../../../../../../graphql/base-types.graphql';

export const OptionsSubscriber = ({
  companyId,
  subscriber,
  setPaymentStatus,
  deleteSubscriber,
  createRepresentative,
  deleteRepresentative,
  editSubscriber,
  warrantProgram,
}: {
  companyId: string;
  setPaymentStatus: ReturnType<typeof useOssSetPaymentStatusMutation>[0];
  deleteSubscriber: ReturnType<typeof useOssDeleteMutation>[0];
  subscriber: GqlStepOsrSubscribersQuery['optionsSharesRegistration']['subscribers'][number];
  createRepresentative: ReturnType<typeof useCreateOptionsSharesSubscriberRepresentativeMutation>[0];
  deleteRepresentative: ReturnType<typeof useDeleteOptionsSharesSubscriberRepresentativeMutation>[0];
  editSubscriber: () => void;
  warrantProgram: Pick<GqlWarrantProgram, 'description' | 'companyId' | 'paidForAt'> & {
    meetingConcern?: Pick<GqlMeetingConcern, 'meetingId'> | null;
  };
}) => {
  const { i18n } = useLingui();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showIdentityDialog, setShowIdentityDialog] = React.useState(false);

  return (
    <Card>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Identity identity={subscriber.identity} />
          <LabeledText label={<Trans id="Shares" />}>
            <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
              {subscriber.quantity}
            </NumberFormat>
          </LabeledText>

          <LabeledText label={<Trans id="Share class" />}>{subscriber.shareClass.name}</LabeledText>

          <ToggleButtonGroup
            value={subscriber.paymentStatus.toString()}
            onChange={async (_event, newValue) => {
              await setPaymentStatus({
                variables: {
                  companyId,
                  input: { id: subscriber.id, paymentStatus: parseInt(newValue ?? '0') },
                },
              });
            }}
          >
            <Button value={PaymentStatus.Paid.toString()}>
              <Trans id="Has paid" />
            </Button>
            <Button value={PaymentStatus.WaitingForPayment.toString()}>
              <Trans id="Waiting for payment" />
            </Button>
            <Button value={PaymentStatus.NotPaid.toString()}>
              <Trans id="Not paid in time" />
            </Button>
          </ToggleButtonGroup>

          <ButtonGroup>
            <IconButton onClick={editSubscriber}>
              <EditIcon />
            </IconButton>

            <IconButton
              color="danger"
              onClick={() => {
                setShowDeleteDialog(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </ButtonGroup>
        </Stack>
        {subscriber.identity.juridicalKind === JuridicalKind.Company && (
          <Stack spacing={1}>
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowIdentityDialog(true);
                }}
              >
                <Trans id="Add representative" />
              </Button>
            </ButtonGroup>

            <Stack direction="row" gap={2}>
              {subscriber.representatives.map(rep => {
                return (
                  <Card key={rep.id}>
                    <Stack direction="row" key={rep.id} gap={1}>
                      <Identity identity={rep.identity} />
                      <IconButton
                        color="danger"
                        onClick={async () => {
                          await deleteRepresentative({ variables: { companyId, id: rep.id } });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          </Stack>
        )}
        <OptionsSharesSubscriptionNote warrantProgram={warrantProgram} subscriber={subscriber} />
      </Stack>
      {showIdentityDialog && (
        <AddIdentity
          headline={<Trans id="Add representative" />}
          juridicalKinds={[JuridicalKind.Person]}
          availableInputStates={{
            [InputState.System]: false,
            [InputState.CompanyBoard]: subscriber.identity.identityNumberKind === IDKind.SwedishOrgNumber,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          companyBoardOrgNumber={subscriber.identity.identityNumber}
          handleCreate={async identity => {
            await createRepresentative({
              variables: {
                companyId,
                input: { optionsSharesSubscriberId: subscriber.id, identityInput: identityToInput(identity) },
              },
            });
            setShowIdentityDialog(false);
          }}
          onClose={() => {
            setShowIdentityDialog(false);
          }}
        />
      )}
      {showDeleteDialog && (
        <ConfirmDialog
          header={<Trans id="Are you sure you want to delete this subscriber?" />}
          confirmProps={{
            children: <Trans id="Delete" />,
            color: 'danger',
          }}
          onCancel={() => {
            setShowDeleteDialog(false);
          }}
          onConfirm={async () => {
            await deleteSubscriber({ variables: { companyId, id: subscriber.id } });
            setShowDeleteDialog(false);
          }}
        />
      )}
    </Card>
  );
};
